import CustomModal from "..";
import { useState } from "react";
import { Button, Accordion, Card, Row, Col, Spinner } from "react-bootstrap";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import { useTranslation } from "react-i18next";

import "./index.scss";

const CooperationConditionCard = ({ condition, index, type, iia }) => {
    const { t } = useTranslation('translation', {keyPrefix: 'nominationList.relevantIIAsButton.coopConditions'})
    const getConditionType = () => {
        if (condition.student_studies && condition.student_studies.length > 0) return t('studentStudy');
        if (condition.student_traineeships && condition.student_traineeships.length > 0) return t('studentTraineeship');
        if (condition.staff_teachers && condition.staff_teachers.length > 0) return t('staffTeacher');
        if (condition.staff_trainings && condition.staff_trainings.length > 0) return t('staffTraining');
        return '';
    };

    const getConditionData = () => {
        if (condition.student_studies && condition.student_studies.length > 0) return condition.student_studies;
        if (condition.student_traineeships && condition.student_traineeships.length > 0) return condition.student_traineeships;
        if (condition.staff_teachers && condition.staff_teachers.length > 0) return condition.staff_teachers;
        if (condition.staff_trainings && condition.staff_trainings.length > 0) return condition.staff_trainings;
        return [];
    };

    const conditionType = getConditionType();
    const conditionData = getConditionData();

    return (
        <Accordion.Item eventKey={index} className="mb-2">
            <Accordion.Header>
                {conditionData.map((item, idx) => {
                    const sendingHeiId = item?.sending_hei_id || 'N/A';
                    const receivingHeiId = item?.receiving_hei_id || 'N/A';
                    const studyFields = item?.subject_area?.map(sa => sa.isced_f_code).join(', ') || 'N/A';
                    
                    return (
                        <div key={idx} style={{ fontSize: '14px', marginBottom: idx < conditionData.length - 1 ? '8px' : '0' }}>
                            <strong>{conditionType}:</strong>{' '}
                            <span style={{ color: '#d63384' }}>{sendingHeiId}</span>
                            {' → '}
                            <span style={{ color: '#d63384' }}>{receivingHeiId}</span>
                            {' / ' + t('studyFields') + ': '}
                            <span>{studyFields}</span>
                        </div>
                    );
                })}
            </Accordion.Header>
            <Accordion.Body>
                {conditionData.map((item, idx) => (
                    <div key={idx} className="mb-3 pb-3" style={{ borderBottom: idx < conditionData.length - 1 ? '1px solid #dee2e6' : 'none' }}>
                        <Row>
                            <Col md={6}>
                                <p className="mb-2"><strong>{t('sendingHei')}:</strong> {item.sending_hei_id}</p>
                                <p className="mb-2"><strong>{t('sendingOrgUnit')}:</strong> {item.sending_ounit_name || item.sending_ounit_id}</p>
                            </Col>
                            <Col md={6}>
                                <p className="mb-2"><strong>{t('receivingHei')}:</strong> {item.receiving_hei_id}</p>
                                <p className="mb-2"><strong>{t('receivingOrgUnit')}:</strong> {item.receiving_ounit_name || item.receiving_ounit_id}</p>
                            </Col>
                        </Row>
                        
                        {item.receiving_acad_year && (
                            <p className="mb-2"><strong>{t('academicYears')}:</strong> {item.receiving_acad_year.join(', ')}</p>
                        )}
                        
                        {item.eqf_level && (
                            <p className="mb-2"><strong>{t('eqfLevels')}:</strong> {item.eqf_level.join(', ')}</p>
                        )}
                        
                        <Row>
                            <Col md={6}>
                                <p className="mb-2"><strong>{t('mobilitiesPerYear')}:</strong> {item.mobilities_per_year}</p>
                            </Col>
                            <Col md={6}>
                                {item.total_months_per_year && (
                                    <p className="mb-2"><strong>{t('totalMonthsPerYear')}:</strong> {item.total_months_per_year}</p>
                                )}
                                {item.total_days_per_year && (
                                    <p className="mb-2"><strong>{t('totalDaysPerYear')}:</strong> {item.total_days_per_year}</p>
                                )}
                            </Col>
                        </Row>

                        {item.subject_area && item.subject_area.length > 0 && (
                            <div className="mb-2">
                                <strong>{t('subjectAreas')}:</strong>
                                <ul className="mb-0 mt-1">
                                    {item.subject_area.map((subject, subIdx) => (
                                        <li key={subIdx}>
                                            {subject.isced_f_code} - {subject.isced_f_code_name}
                                            {subject.isced_clarification && ` (${subject.isced_clarification})`}
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        )}
                    </div>
                ))}
            </Accordion.Body>
        </Accordion.Item>
    );
};

const RelatedIIAsModal = ({ nomination, type, loadingIIAs = false }) => {
    const [showModal, setShowModal] = useState(false);

    const { t } = useTranslation('translation', {keyPrefix: 'nominationList.relevantIIAsButton'});

    if (nomination?.field_mobility_details?.length === 1) {
        // Get IIA count from the new structure
        const relatedIIAsCount = nomination?.related_iias?.length || 0;
        const hasIIAs = relatedIIAsCount > 0;
        
        // Determine the partner institution name
        const partnerName = type === "outgoing" 
            ? nomination?.field_mobility_details[0]?.field_host_name 
            : nomination?.field_mobility_details[0]?.field_home_name;
        
        return (
            <>
                <OverlayTrigger
                    placement="top"
                    overlay={
                        hasIIAs || loadingIIAs ? 
                        <></> : 
                        <Tooltip className="position-absolute">
                            {t('noIIAsFound.part1') + partnerName + t('noIIAsFound.part2')}
                        </Tooltip>
                    }
                >
                    <span className="d-inline-block">
                        <Button
                            className={"related-iias-primary-blue"}
                            type={"button"}
                            id={"related-iias-button-medium"}
                            onClick={() => setShowModal(true)}
                            disabled={loadingIIAs || (!hasIIAs && !loadingIIAs)}
                        >
                            {loadingIIAs ? (
                                <>
                                    <Spinner animation="border" size="sm" className="me-2" />
                                    {t('loadingIIAs')}
                                </>
                            ) : (
                                t('buttonLabel')
                            )}
                        </Button>
                    </span>
                </OverlayTrigger>

                <CustomModal
                    show={showModal}
                    handleClose={() => setShowModal(false)}
                    title={
                        loadingIIAs 
                            ? t('loadingWith') + partnerName + '...'
                            : t('IIAsfound.part1') + relatedIIAsCount + t('IIAsfound.part2') + partnerName + t('IIAsfound.part3')
                    }
                    body={
                        loadingIIAs ? (
                            <div className="text-center py-5">
                                <Spinner animation="border" role="status">
                                    <span className="visually-hidden">{t('loading')}</span>
                                </Spinner>
                                <p className="mt-3">{t('loadingRelatedIIAs')}</p>
                            </div>
                        ) : (
                        <div>
                            {nomination?.related_iias && nomination.related_iias.length > 0 ? (
                                    nomination.related_iias.map((iia, index) => (
                                <Card key={iia.id || index} className="mb-3 shadow-sm">
                                    <Card.Body>
                                    <div className="d-flex justify-content-between align-items-center mb-3">
                                        <h5 className="mb-0">IIA ID: {iia.iia_id || iia.field_your_iia_id || 'N/A'}</h5>
                                        <Button
                                            className="related-iias-primary-blue"
                                            id="view-full-iia-button"
                                            onClick={() => window.open("/iias/single-view/" + iia.id, "_blank")}
                                        >
                                            {t('viewFullIIA')}
                                        </Button>
                                    </div>

                                        <Row className="mb-3">
                                            <Col md={6}>
                                                <p className="mb-2"><strong>{t('iiaView.status')}:</strong> {iia.iia_status ? 'Approved by all' : 'N/A'}</p>
                                                <p className="mb-2"><strong>{t('iiaView.yourID')}:</strong> {iia.field_your_iia_id || 'N/A'}</p>
                                            </Col>
                                            <Col md={6}>
                                                <p className="mb-2"><strong>{t('iiaView.partnerID')}:</strong> {iia.field_partner_iia_id || 'N/A'}</p>
                                            </Col>
                                        </Row>

                                        {iia.cooperation_conditions && (
                                            (iia.cooperation_conditions.student_studies?.length > 0 ||
                                             iia.cooperation_conditions.student_traineeships?.length > 0 ||
                                             iia.cooperation_conditions.staff_teachers?.length > 0 ||
                                             iia.cooperation_conditions.staff_trainings?.length > 0) ? (
                                                <>
                                                    <h6 className="mb-3">{t('coopConditions.title')}:</h6>
                                                    <Accordion>
                                                        {iia.cooperation_conditions.student_studies && iia.cooperation_conditions.student_studies.length > 0 && (
                                                            <CooperationConditionCard 
                                                                condition={{ student_studies: iia.cooperation_conditions.student_studies }} 
                                                                index={`${index}-0`} 
                                                                type={type}
                                                                iia={iia}
                                                            />
                                                        )}
                                                        {iia.cooperation_conditions.student_traineeships && iia.cooperation_conditions.student_traineeships.length > 0 && (
                                                            <CooperationConditionCard 
                                                                condition={{ student_traineeships: iia.cooperation_conditions.student_traineeships }} 
                                                                index={`${index}-1`} 
                                                                type={type}
                                                                iia={iia}
                                                            />
                                                        )}
                                                        {iia.cooperation_conditions.staff_teachers && iia.cooperation_conditions.staff_teachers.length > 0 && (
                                                            <CooperationConditionCard 
                                                                condition={{ staff_teachers: iia.cooperation_conditions.staff_teachers }} 
                                                                index={`${index}-2`} 
                                                                type={type}
                                                                iia={iia}
                                                            />
                                                        )}
                                                        {iia.cooperation_conditions.staff_trainings && iia.cooperation_conditions.staff_trainings.length > 0 && (
                                                            <CooperationConditionCard 
                                                                condition={{ staff_trainings: iia.cooperation_conditions.staff_trainings }} 
                                                                index={`${index}-3`} 
                                                                type={type}
                                                                iia={iia}
                                                            />
                                                        )}
                                                    </Accordion>
                                                </>
                                            ) : (
                                                <p className="text-muted">{t('coopConditions.noConditionsFound')}</p>
                                            )
                                        )}
                                    </Card.Body>
                                </Card>
                                    ))
                                ) : (
                                    <div className="alert alert-info">
                                        {t('noIIAsfound.message') + partnerName + '.'}
                                    </div>
                                )}
                        </div>
                        )
                    }
                />
            </>
        );
    }

    return null;
};

export default RelatedIIAsModal;